import React, { useState, useMemo } from "react";
import { Box, Typography, TextField, MenuItem } from "@mui/material";
import { getCountryCallingCode, CountryCode } from "libphonenumber-js";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";

const countryOptions = countries
  .map((country) => {
    try {
      const callingCode = getCountryCallingCode(country.code as CountryCode);
      return {
        label: `${country.label} (+${callingCode})`,
        value: country.code,
        callingCode,
      };
    } catch {
      return null;
    }
  })
  .filter(Boolean) as Array<{
  label: string;
  value: string;
  callingCode: string;
}>;

interface CountryCodePhoneFieldProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (phone: string) => void;
  error?: boolean;
  disabled?: boolean;
  compact?: boolean;
}

const CountryCodePhoneField: React.FC<CountryCodePhoneFieldProps> = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error = false,
  disabled = false,
  compact = false,
}) => {
  const [searchTerm] = useState("");

  const filteredCountries = useMemo(
    () =>
      countryOptions.filter(
        (c) =>
          c.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.callingCode.includes(searchTerm)
      ),
    [searchTerm]
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle1"
        fontWeight={400}
        color={COLORS.BlackText}
        sx={{ mb: 1 }}
      >
        Mobile Number
      </Typography>

      <Box display="flex" gap={1} alignItems="center">
        <Box sx={{ flex: compact || disabled ? '0 0 100px' : 4 }}>
          <TextField
            select
            fullWidth
            value={countryCode}
            onChange={(e) => onCountryCodeChange(e.target.value)}
            variant="outlined"
            size="small"
            disabled={disabled}
            sx={{
              backgroundColor: COLORS.textFieldBackgroundColor,
              borderRadius: "12px",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": {
                  borderRadius: "12px",
                  border: "2px solid " + COLORS.textFieldBackgroundColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#868585",
                  borderWidth: "2px",
                },
              },
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return <span style={{ color: '#999' }}>Select code...</span>;
                }
                const country = filteredCountries.find(c => c.value === selected);
                return country?.label || (selected as string);
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    width: 190,
                    maxHeight: 180,
                    mt: 1,
                    overflowY: "auto",
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select code...</em>
            </MenuItem>
            {filteredCountries.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box flex={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder=""
            value={phoneNumber}
            onChange={(e) => onPhoneNumberChange(e.target.value)}
            disabled={disabled}
            error={error}
            size="small"
            sx={{
              backgroundColor: COLORS.textFieldBackgroundColor,
              borderRadius: "12px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": {
                  borderRadius: "12px",
                  border: "2px solid " + COLORS.textFieldBackgroundColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#868585",
                  borderWidth: "2px",
                },
              },
            }}
          />
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: "red", fontSize: "12px", mt: 0.5 }}>
          Invalid phone number
        </Typography>
      )}
    </Box>
  );
};

export default CountryCodePhoneField;
